import {
  exportRecords,
  fetchRecords,
  Market,
  RecordFilters,
  ReferralRecord,
  ReferralStatus,
} from "api/admin/referrals";
import Table from "components/ui/Table";
import useAdminAccess from "hooks/useAdminAccess";
import { FormEvent, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReferralActions, { Decision } from "./ReferralActions";
import {
  Button,
  Card,
  Field,
  Pages,
  ProgressView,
  UserLink,
  dateWindow,
  downloadCsv,
  errorMessage,
  fieldClass,
  formatDate,
  formatLabel,
  markets,
  money,
  queryOptions,
  useReferralKey,
} from "./shared";
export function RecordTable({
  records,
  choose,
}: {
  records: ReferralRecord[];
  choose?: (decision: Decision) => void;
}) {
  const { can } = useAdminAccess();
  return (
    <div className="overflow-x-auto">
      <Table
        header={[
          "Referral",
          "Referrer",
          "Referee",
          "Status",
          "Reward",
          "Details",
        ]}
        emptyMessage="No referrals found."
        data={records.map((row) => [
          `#${row.id}`,
          <UserLink id={row.referrerId}>
            {row.referrer.userName || `User #${row.referrerId}`}
          </UserLink>,
          <UserLink id={row.refereeId}>
            {row.referee.userName || `User #${row.refereeId}`}
          </UserLink>,
          <span>
            {formatLabel(row.status)}
            {row.onHold ? " · Review hold" : ""}
            {row.rewardReversedAt ? " · Reversed" : ""}
          </span>,
          row.rewardAmountMinor !== null && row.rewardCurrency
            ? money(row.rewardAmountMinor, row.rewardCurrency)
            : "—",
          <details>
            <summary className="cursor-pointer underline">
              View #{row.id}
            </summary>
            <div className="min-w-[260px] space-y-3 mt-3">
              <p>
                Referrer market: {row.referrerMarket} · Referee market:{" "}
                {row.refereeMarket}
              </p>
              <p>Signed up: {formatDate(row.createdAt)}</p>
              <p>
                Referrer: {row.referrer.email || "—"} · Referee:{" "}
                {row.referee.email || "—"}
              </p>
              <ProgressView
                progress={row.progress}
                currency={row.refereeMarket}
              />
              <p>
                Open flags: {row.openFlagCount} · Qualified:{" "}
                {formatDate(row.qualifiedAt)}
              </p>
              <p>
                Rewarded: {formatDate(row.rewardedAt)} · Cycle:{" "}
                {row.cycleId ?? "—"}
              </p>
              <p>
                Resolved: {formatDate(row.resolvedAt)} · Reason:{" "}
                {formatLabel(row.reasonCode)}
              </p>
              {row.rewardReversedAt && (
                <p>
                  Reversed: {formatDate(row.rewardReversedAt)} · Wallet lien:{" "}
                  {money(
                    row.rewardReversalLienMinor ?? 0,
                    row.rewardCurrency ?? row.referrerMarket,
                  )}
                </p>
              )}
              {choose && (
                <div className="flex flex-wrap gap-2">
                  {row.status === "pending" &&
                    can("referrals.reviews.approve") && (
                      <>
                        {row.onHold && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              choose({ id: row.id, action: "clear-hold" })
                            }
                          >
                            Clear hold
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          onClick={() =>
                            choose({ id: row.id, action: "reject" })
                          }
                        >
                          Reject referral
                        </Button>
                      </>
                    )}
                  {row.status === "successful" &&
                    !row.rewardReversedAt &&
                    can("referrals.rewards.approve") && (
                      <Button
                        variant="danger"
                        onClick={() =>
                          choose({ id: row.id, action: "reverse" })
                        }
                      >
                        Reverse reward
                      </Button>
                    )}
                </div>
              )}
            </div>
          </details>,
          undefined,
        ])}
      />
    </div>
  );
}
export default function Records() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [referrer, setReferrer] = useState(params.get("referrer") ?? ""),
    [referee, setReferee] = useState("");
  const [market, setMarket] = useState(""),
    [status, setStatus] = useState(""),
    [hold, setHold] = useState("");
  const [from, setFrom] = useState(""),
    [before, setBefore] = useState("");
  const [filters, setFilters] = useState<RecordFilters>({
    referrer: params.get("referrer") || undefined,
  });
  const [page, setPage] = useState(1),
    [error, setError] = useState("");
  const [decision, setDecision] = useState<Decision>(),
    [message, setMessage] = useState("");
  const { can } = useAdminAccess();
  const key = useReferralKey();
  const query = useQuery(
    [...key, "records", filters, page],
    () => fetchRecords(filters, page),
    queryOptions,
  );
  const csv = useMutation(exportRecords, {
    onSuccess: (text) => downloadCsv(text, "referral-records.csv"),
    retry: false,
  });
  const apply = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      if ((from || before) && !(from && before))
        throw new Error("Choose both sign-up dates, or leave both blank.");
      const range = from && before ? dateWindow(from, before) : undefined;
      setFilters({
        referrer: referrer.trim() || undefined,
        referee: referee.trim() || undefined,
        market: (market as Market) || undefined,
        status: (status as ReferralStatus) || undefined,
        onHold: hold ? hold === "true" : undefined,
        signedUpFrom: range?.from,
        signedUpBefore: range?.to,
      });
      setPage(1);
      setDecision(undefined);
      setMessage("");
      csv.reset();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <div className="space-y-5">
      <Card
        title="Referral records"
        description="Search exact user ID, username, email or phone. Market filters refer to the referrer's market."
      >
        <form className="p-5" onSubmit={apply}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Referrer">
              <input
                className={fieldClass}
                maxLength={255}
                value={referrer}
                onChange={(e) => setReferrer(e.target.value)}
              />
            </Field>
            <Field label="Referee">
              <input
                className={fieldClass}
                maxLength={255}
                value={referee}
                onChange={(e) => setReferee(e.target.value)}
              />
            </Field>
            <Field label="Market">
              <select
                className={fieldClass}
                value={market}
                onChange={(e) => setMarket(e.target.value)}
              >
                <option value="">All markets</option>
                {markets.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                className={fieldClass}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All statuses</option>
                {["pending", "successful", "capped", "rejected"].map((s) => (
                  <option key={s} value={s}>
                    {formatLabel(s)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Review hold">
              <select
                className={fieldClass}
                value={hold}
                onChange={(e) => setHold(e.target.value)}
              >
                <option value="">Any</option>
                <option value="true">On hold</option>
                <option value="false">Not on hold</option>
              </select>
            </Field>
            <Field label="Signed up from (UTC)">
              <input
                type="date"
                className={fieldClass}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Field>
            <Field label="Signed up before (UTC, exclusive)">
              <input
                type="date"
                className={fieldClass}
                value={before}
                onChange={(e) => setBefore(e.target.value)}
              />
            </Field>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <Button type="submit">Apply filters</Button>
            {can("referrals.records.export") && (
              <Button
                type="button"
                variant="secondary"
                disabled={
                  csv.isLoading ||
                  !filters.signedUpFrom ||
                  !filters.signedUpBefore
                }
                onClick={() => csv.mutate(filters)}
              >
                {csv.isLoading ? "Exporting…" : "Export records CSV"}
              </Button>
            )}
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Export uses the applied filters. Apply a sign-up range of up to 366
            days; exports are limited to 50,000 records.
          </p>
          {(error || csv.isError) && (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {error || errorMessage(csv.error)}
            </p>
          )}
        </form>
      </Card>
      <form
        aria-label="Participant lookup"
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (Number.isSafeInteger(Number(userId)) && Number(userId) > 0)
            navigate(`/referrals/users/${userId}`);
        }}
      >
        <Field label="Participant user ID">
          <input
            type="number"
            min="1"
            step="1"
            required
            className={fieldClass}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="secondary">
          View participant
        </Button>
      </form>
      {message && (
        <p role="status" className="text-green-700">
          {message}
        </p>
      )}
      {decision && (
        <ReferralActions
          decision={decision}
          cancel={() => setDecision(undefined)}
          done={(text) => {
            setMessage(text);
            setDecision(undefined);
          }}
        />
      )}
      {query.isLoading || query.isError ? (
        <Table
          header={["Referrals"]}
          data={[]}
          isLoading={query.isLoading}
          errorMessage={query.isError ? errorMessage(query.error) : undefined}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <RecordTable
          records={query.data?.results ?? []}
          choose={(value) => {
            setDecision(value);
            setMessage("");
          }}
        />
      )}
      <Pages
        meta={query.data?.meta}
        busy={query.isFetching}
        change={(p) => {
          setPage(p);
          setDecision(undefined);
        }}
      />
    </div>
  );
}
